<template>
  <el-dialog
    title="导入结果"
    :visible.sync="innerVisible"
    width="760px"
    append-to-body
    :close-on-click-modal="false"
    @closed="handleClosed"
  >
    <el-alert
      v-if="summary"
      :title="summary"
      type="info"
      :closable="false"
      show-icon
      class="import-result-dialog__alert"
    />
    <template v-if="items.length">
      <div class="import-result-dialog__toolbar">
        <div class="import-result-dialog__stats">
          <template v-if="stats">
            共 {{ stats.totalCount }} 条，
            成功 <span class="is-success">{{ stats.successCount }}</span> 条，
            失败 <span class="is-danger">{{ stats.failCount }}</span> 条
          </template>
          <template v-else>
            失败 <span class="is-danger">{{ items.length }}</span> 条，
            筛选后 <span class="is-success">{{ filteredItems.length }}</span> 条
          </template>
        </div>
        <el-input
          v-model.trim="keyword"
          clearable
          placeholder="搜索失败原因"
          size="small"
          class="import-result-dialog__search"
          @input="handleKeywordChange"
        >
          <i slot="prefix" class="el-input__icon el-icon-search"></i>
        </el-input>
      </div>
      <el-table
        :data="pagedItems"
        border
        stripe
        size="small"
        class="import-result-dialog__table"
        max-height="400"
        empty-text="暂无匹配明细"
      >
        <el-table-column
          v-if="showRowIndexColumn"
          prop="rowIndex"
          label="行号"
          width="64"
          align="center"
        />
        <el-table-column label="#" width="56" align="center">
          <template slot-scope="scope">
            {{ (page - 1) * currentPageSize + scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column
          v-if="showCategoryColumn"
          prop="category"
          label="类型"
          width="110"
          show-overflow-tooltip
        />
        <el-table-column prop="message" label="失败原因" min-width="280" show-overflow-tooltip />
      </el-table>
      <div v-if="filteredItems.length" class="import-result-dialog__pager">
        <el-pagination
          background
          small
          layout="total, sizes, prev, pager, next"
          :current-page="page"
          :page-sizes="[8, 16, 24, 32]"
          :page-size="currentPageSize"
          :total="filteredItems.length"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </template>
    <el-empty v-else description="全部导入成功，无失败明细" />
    <span slot="footer" class="dialog-footer">
      <el-button type="primary" @click="innerVisible = false">我知道了</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { normalizeImportBatchResult, normalizeImportBatchResponse, parseImportResultMessage } from '../utils/importResultParser';

export default {
  name: 'ImportResultDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    /** 结构化 ImportBatchResultDto */
    result: {
      type: Object,
      default: null
    },
    /** 兼容旧版：仅传 msg 字符串 */
    message: {
      type: String,
      default: ''
    },
    /** 每页条数，默认 8 条。最后更新：2026-09-07 */
    pageSize: {
      type: Number,
      default: 8
    }
  },
  data() {
    return {
      innerVisible: false,
      summary: '',
      items: [],
      stats: null,
      keyword: '',
      page: 1,
      currentPageSize: this.pageSize || 8
    };
  },
  computed: {
    showRowIndexColumn() {
      return (this.items || []).some(item => item.rowIndex > 0);
    },
    showCategoryColumn() {
      const categories = new Set((this.items || []).map(item => item.category).filter(Boolean));
      return categories.size > 1 || (categories.size === 1 && !categories.has('失败'));
    },
    filteredItems() {
      const keyword = (this.keyword || '').trim().toLowerCase();
      if (!keyword) {
        return this.items || [];
      }
      return (this.items || []).filter(item => {
        const haystack = `${item.rowIndex || ''} ${item.category || ''} ${item.message || ''} ${item.key || ''}`.toLowerCase();
        return haystack.indexOf(keyword) >= 0;
      });
    },
    pagedItems() {
      const list = this.filteredItems || [];
      const currentPage = this.page > 0 ? this.page : 1;
      const size = this.currentPageSize > 0 ? this.currentPageSize : 8;
      const start = (currentPage - 1) * size;
      return list.slice(start, start + size);
    }
  },
  watch: {
    pageSize: {
      immediate: true,
      handler(val) {
        if (val && val > 0) {
          this.currentPageSize = val;
        }
      }
    },
    visible: {
      immediate: true,
      handler(val) {
        this.innerVisible = val;
        if (val) {
          this.applyPayload();
        }
      }
    },
    innerVisible(val) {
      this.$emit('update:visible', val);
    },
    result() {
      if (this.innerVisible) {
        this.applyPayload();
      }
    },
    message() {
      if (this.innerVisible) {
        this.applyPayload();
      }
    }
  },
  methods: {
    /** 根据结构化结果或旧版 msg 展示。最后更新：2026-09-02 */
    applyPayload() {
      let normalized;
      if (this.result) {
        normalized = normalizeImportBatchResult(this.result);
      } else if (this.message) {
        normalized = parseImportResultMessage(this.message);
      } else {
        normalized = { summary: '', items: [], stats: null };
      }
      this.summary = normalized.summary;
      this.items = normalized.items;
      this.stats = normalized.stats;
      this.keyword = '';
      this.page = 1;
      this.currentPageSize = this.pageSize || 8;
    },
    /** 供父组件通过 ref 直接打开 */
    openFromResponse(resData) {
      const normalized = normalizeImportBatchResponse(resData);
      this.summary = normalized.summary;
      this.items = normalized.items;
      this.stats = normalized.stats;
      this.keyword = '';
      this.page = 1;
      this.currentPageSize = this.pageSize || 8;
      this.innerVisible = true;
    },
    handleKeywordChange() {
      this.page = 1;
    },
    handleSizeChange(val) {
      this.currentPageSize = val;
      this.page = 1;
    },
    handlePageChange(page) {
      this.page = page;
    },
    handleClosed() {
      this.keyword = '';
      this.page = 1;
      this.currentPageSize = this.pageSize || 8;
    }
  }
};
</script>

<style scoped>
.import-result-dialog__alert {
  margin-bottom: 12px;
}

.import-result-dialog__toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.import-result-dialog__stats {
  font-size: 13px;
  color: #606266;
  flex-shrink: 0;
}

.import-result-dialog__stats .is-danger {
  color: #f56c6c;
  font-weight: 600;
}

.import-result-dialog__stats .is-success {
  color: #67c23a;
  font-weight: 600;
}

.import-result-dialog__search {
  width: 280px;
}

.import-result-dialog__pager {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
}

/* 优化表格内部滚动条质感。最后更新：2026-09-07 */
.import-result-dialog__table >>> .el-table__body-wrapper::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.import-result-dialog__table >>> .el-table__body-wrapper::-webkit-scrollbar-thumb {
  background: #dcdfe6;
  border-radius: 3px;
}
.import-result-dialog__table >>> .el-table__body-wrapper::-webkit-scrollbar-thumb:hover {
  background: #c0c4cc;
}
</style>
